import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  getAllShiftsOverview,
  getShiftAssignmentsOverview,
  getMyShiftAssignmentsOverview,
  getShiftsRoster,
  type RosterMember,
} from '@/api/services/shifts.service'
import type {
  Shift,
  ShiftAssignment,
  ShiftFilterQueryDto,
  ShiftAssignmentFilterQueryDto,
} from '@/types/shift.types'

export interface CompanyScopedShift extends Shift {
  companyId: string
}

export interface CompanyScopedAssignment extends ShiftAssignment {
  companyId: string
}

export interface AcrossCompaniesShiftsResult {
  shifts: CompanyScopedShift[]
  isLoading: boolean
  isError: boolean
  error: Error | null
  /** Re-runs the overview query; wire to an error state's retry. */
  refetch: () => Promise<unknown>
}

export interface AcrossCompaniesAssignmentsResult {
  assignments: CompanyScopedAssignment[]
  isLoading: boolean
  /** True while the previous result is shown and the requested one loads. */
  isPlaceholderData: boolean
  isError: boolean
  error: Error | null
  /** Re-runs the overview query; wire to an error state's retry. */
  refetch: () => Promise<unknown>
}

const overviewKeys = {
  shifts: (companyIds: string[], params?: ShiftFilterQueryDto) =>
    ['shifts-overview', [...companyIds].sort().join(','), params] as const,
  assignments: (companyIds: string[], params?: ShiftAssignmentFilterQueryDto) =>
    ['shift-assignments-overview', [...companyIds].sort().join(','), params] as const,
  mine: (companyIds: string[], params?: ShiftAssignmentFilterQueryDto) =>
    ['shift-assignments-overview-me', [...companyIds].sort().join(','), params] as const,
}

/**
 * Cross-company shift list. One backend call regardless of how many companies the
 * caller is filtering on. The companyId is preserved on each row so the FE can
 * group/colour client-side.
 */
// Shared empty lists, so an empty result doesn't hand the grid a new array each render.
const NO_SHIFTS: CompanyScopedShift[] = []
const NO_ASSIGNMENTS: CompanyScopedAssignment[] = []
const NO_MEMBERS: RosterMember[] = []

export function useShiftsAcrossCompanies(
  companyIds: string[],
  params?: ShiftFilterQueryDto
): AcrossCompaniesShiftsResult {
  const enabled = companyIds.length > 0
  const query = useQuery({
    queryKey: overviewKeys.shifts(companyIds, params),
    queryFn: () => getAllShiftsOverview({ ...(params ?? {}), companyIds }),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled,
  })
  return {
    shifts: (query.data ?? NO_SHIFTS) as CompanyScopedShift[],
    isLoading: enabled && query.isLoading,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    refetch: query.refetch,
  }
}

/**
 * Cross-company assignment list. Pass `mineOnly` for the self-service variant
 * (the backend scopes it to the caller's memberships automatically).
 */
export function useShiftAssignmentsAcrossCompanies(
  companyIds: string[],
  params?: ShiftAssignmentFilterQueryDto,
  options?: {
    mineOnly?: boolean
    allowAllCompanies?: boolean
    /** Keep the last result on screen while a new range or filter loads. */
    keepPrevious?: boolean
  }
): AcrossCompaniesAssignmentsResult {
  const mineOnly = options?.mineOnly === true
  const enabled = companyIds.length > 0 || mineOnly || options?.allowAllCompanies === true
  const query = useQuery({
    queryKey: mineOnly
      ? overviewKeys.mine(companyIds, params)
      : overviewKeys.assignments(companyIds, params),
    queryFn: () =>
      mineOnly
        ? getMyShiftAssignmentsOverview({ ...(params ?? {}), companyIds })
        : getShiftAssignmentsOverview({ ...(params ?? {}), companyIds }),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled,
    placeholderData: options?.keepPrevious ? keepPreviousData : undefined,
  })
  return {
    assignments: (query.data?.data ?? NO_ASSIGNMENTS) as CompanyScopedAssignment[],
    isLoading: enabled && query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    refetch: query.refetch,
  }
}

export interface RosterResult {
  members: RosterMember[]
  assignments: CompanyScopedAssignment[]
  isLoading: boolean
  /** True while the previous range is shown and the requested one loads. */
  isPlaceholderData: boolean
  isError: boolean
}

const rosterKey = (
  companyIds: string[],
  params?: ShiftAssignmentFilterQueryDto
) => ['shifts-roster', [...companyIds].sort().join(','), params] as const

/**
 * Single round-trip fetch of (members, assignments) across companies.
 * Use when the planning grid needs to render rows for staff with zero shifts in the range.
 */
export function useShiftsRoster(
  companyIds: string[],
  params?: ShiftAssignmentFilterQueryDto
): RosterResult {
  const enabled = companyIds.length > 0
  const query = useQuery({
    queryKey: rosterKey(companyIds, params),
    queryFn: () => getShiftsRoster({ ...(params ?? {}), companyIds }),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled,
    // The planning grid keeps showing the last period while the next one loads,
    // instead of dropping to a skeleton and rebuilding every row from scratch.
    placeholderData: keepPreviousData,
  })
  return {
    members: query.data?.members ?? NO_MEMBERS,
    assignments: (query.data?.assignments ?? NO_ASSIGNMENTS) as CompanyScopedAssignment[],
    isLoading: enabled && query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
  }
}
